import type { AuditTrailGetRequestDto } from './dto/auditTrail-get.request.dto.js';
import type { UpdateFactRequestDto } from '../matter/fact/dto/request-update-fact.dto.js';
import type { SetKeyRequestDto } from '../matter/fact/dto/request-set-key.dto.js';
import type { MatterUpdateMetadataRequestDto } from '../matter/dto/matter-update-metadata.request.dto.js';
import type { UpdateSubsetRequestDto } from '../matter/subset/dto/update-subset.request.dto.js';
import type { UpdateChronologyRequestDto } from '../matter/subset/chronology/dto/request-update.chronology.dto.js';
import type { UpdateDocChronRequestDto } from '../matter/subset/chronology/doc-chronology/dto/update-doc-chron-title-request.dto.js';
import type { UpdateDocChronDocRequestDto } from '../matter/subset/chronology/doc-chronology/dto/update-doc-chron-doc-request.dto.js';
import type { UpdatePersonDto } from '../matter/person/dto/update-person.request.dto.js';

import type { AuditEventType } from '../shared/action-registry.js';
export { AuditEventType } from '../shared/action-registry.js';

// --- AuditDetail discriminated union ---

// Union of all potential types with auditEventType as the discriminator
// Grouped by similar structure for easier parsing
export type AuditDetail =
  | EntityDetail
  | SubsetEntityDetail
  | CountDetail
  | SubsetCountDetail
  | UpdateFactDetail
  | EditMatterDetail
  | EditSubsetDetail
  | EditPersonDetail
  | EditChronologyDetail
  | UpdateDocChronologyDetail
  | UpdateDocChronologyDocDetail
  | RelationshipDetail
  | RelationshipMatterDetail
  | ChronologyRelationshipDetail
  | DocChronologyDocRelationshipDetail
  | TogglePermissionOverridesDetail
  | PermissionOverrideDetail
  | ResetPermissionOverridesDetail
  | UpdateAdditionalContextDetail
  | RelativitySyncDetail
  | RelativityParentChildSyncDetail
  | RunReprocessDetail
  | DownloadDetail
  | ProceduralFileUploadDetail
  | ProceduralSuggestionResolutionDetail
  | ProceduralEventMutationDetail;

// Entity detail - for create/update/delete of named entities (matter-level)

type BaseDetail = {
  name: string;
};
type SubsetId = {
  subsetId: string;
};
type EntityDetail = {
  auditEventType:
    | typeof AuditEventType.MatterCreate
    | typeof AuditEventType.MatterDelete
    | typeof AuditEventType.SubsetCreate
    | typeof AuditEventType.SubsetDelete
    | typeof AuditEventType.DocumentDelete
    | typeof AuditEventType.InconsistencyDelete
    | typeof AuditEventType.RealtimeUse;
} & BaseDetail;

// Entity detail for subset-nested entities (requires subsetId for routing)
type SubsetEntityDetail = {
  auditEventType:
    | typeof AuditEventType.ThreadCreate
    | typeof AuditEventType.ChronologyDelete
    | typeof AuditEventType.DocChronologyDelete
    | typeof AuditEventType.ThreadDelete
    | typeof AuditEventType.FactCreate
    | typeof AuditEventType.FactDelete
    | typeof AuditEventType.InconsistencyReportCreate
    | typeof AuditEventType.InconsistencyReportDelete
    | typeof AuditEventType.KimCreateThread
    | typeof AuditEventType.KimDeleteThread
    | typeof AuditEventType.PersonDelete;
} & BaseDetail &
  SubsetId;

// Count detail - for batch creation operations (matter-level)
type CountDetail = {
  auditEventType: typeof AuditEventType.RunCreate | typeof AuditEventType.DocumentUpload;
  count: number;
} & BaseDetail;

// Created with X docs
type SubsetCountDetail = {
  auditEventType:
    | typeof AuditEventType.ChronologyCreate
    | typeof AuditEventType.DocChronologyCreate;
  count: number;
} & BaseDetail &
  SubsetId;

// Update detail - for fact field updates (now includes optional subsetId/targetId for deep-linking)

type Update = {
  field: string;
  to: string;
  from: string;
};
type EditMatterDetail = {
  auditEventType: typeof AuditEventType.MatterEdit;
  field: keyof MatterUpdateMetadataRequestDto;
} & Update;

type EditSubsetDetail = {
  auditEventType: typeof AuditEventType.SubsetEdit;
  field: keyof UpdateSubsetRequestDto;
} & Update;

type UpdateFactDetail = {
  auditEventType: typeof AuditEventType.FactUpdate;
  field: keyof UpdateFactRequestDto | keyof Omit<SetKeyRequestDto, 'factIds'>;
} & Update &
  SubsetId;

type EditPersonDetail = {
  auditEventType: typeof AuditEventType.PersonUpdate;
  field: keyof UpdatePersonDto;
} & Update &
  SubsetId;

type EditChronologyDetail = {
  auditEventType: typeof AuditEventType.ChronologyEdit;
  field: keyof UpdateChronologyRequestDto;
} & Update &
  SubsetId;

type UpdateDocChronologyDetail = {
  auditEventType: typeof AuditEventType.DocChronologyEdit;
  field: keyof UpdateDocChronRequestDto;
} & Update &
  SubsetId;

type UpdateDocChronologyDocDetail = {
  auditEventType: typeof AuditEventType.DocChronologyUpdateDoc;
  field: keyof UpdateDocChronDocRequestDto;
  docId: string;
  chronologyName: string;
} & Update &
  SubsetId;

// Relationship detail - for adding items to other entities
type Relationship = {
  sourceId: string;
  sourceName: string;
  sourceOwnerName?: string;
};
type RelationshipMatterDetail = {
  auditEventType:
    | typeof AuditEventType.MatterAddUsers
    | typeof AuditEventType.MatterRemoveUsers
    | typeof AuditEventType.MatterAddIssue
    | typeof AuditEventType.MatterRemoveIssue
    | typeof AuditEventType.MatterAddParty
    | typeof AuditEventType.MatterRemoveParty;
} & Relationship;
type RelationshipDetail = {
  auditEventType:
    | typeof AuditEventType.CommentAdd
    | typeof AuditEventType.CommentUpdate
    | typeof AuditEventType.CommentDelete
    | typeof AuditEventType.SubsetAddIssue
    | typeof AuditEventType.SubsetRemoveIssue;
} & Relationship &
  SubsetId;

type ChronologyRelationshipDetail = {
  auditEventType:
    | typeof AuditEventType.ChronologyVerifyFact
    | typeof AuditEventType.ChronologyAddFact;
  chronologyName: string;
} & Relationship &
  SubsetId;

// Doc Chronology Doc relationship detail - includes docId and chronologyId for linking
type DocChronologyDocRelationshipDetail = {
  auditEventType:
    | typeof AuditEventType.DocChronologyAddFact
    | typeof AuditEventType.DocChronologyRemoveFact
    | typeof AuditEventType.DocChronologyAddDocument
    | typeof AuditEventType.DocChronologyRemoveDocument;
  docId: string;
  chronologyName: string;
} & Relationship &
  SubsetId;

// Additional context update detail
type UpdateAdditionalContextDetail = {
  auditEventType: typeof AuditEventType.MatterUpdateAdditionalContext;
  from: string;
  to: string;
};

// Permission override audit details
type TogglePermissionOverridesDetail = {
  auditEventType: typeof AuditEventType.MatterTogglePermissionOverrides;
  field: 'permissionOverridesEnabled';
  from: string;
  to: string;
};

type PermissionOverrideDetail = {
  auditEventType:
    | typeof AuditEventType.MatterSetPermissionOverride
    | typeof AuditEventType.MatterRemovePermissionOverride;
  sourceId: string;
  sourceName: string;
  roleName: string;
};

type ResetPermissionOverridesDetail = {
  auditEventType: typeof AuditEventType.MatterResetPermissionOverrides;
  count: number;
};

type RelativitySyncDetail = {
  auditEventType: typeof AuditEventType.RelativitySync;
  workspaceId: number;
  includeExisting: boolean;
  filesFound: number;
};

type RelativityParentChildSyncDetail = {
  auditEventType: typeof AuditEventType.RelativityBuildParentChildRelationships;
  workspaceId: number;
  newRelationshipsCreated: number;
  existingRelationshipsSkipped: number;
};

type RunReprocessDetail = {
  auditEventType: typeof AuditEventType.RunReprocess;
  parentRunId: string;
  count: number;
} & BaseDetail;

type ProceduralFileUploadDetail = {
  auditEventType: typeof AuditEventType.ProceduralTimelineFileUpload;
  fileId: string;
  fileName: string;
};

type ProceduralSuggestionResolutionDetail = {
  auditEventType:
    | typeof AuditEventType.ProceduralTimelineSuggestionAccept
    | typeof AuditEventType.ProceduralTimelineSuggestionReject;
  suggestionId: string;
  kind: string;
};

type ProceduralEventMutationDetail = {
  auditEventType:
    | typeof AuditEventType.ProceduralTimelineEventCreate
    | typeof AuditEventType.ProceduralTimelineEventUpdate
    | typeof AuditEventType.ProceduralTimelineEventDelete;
  eventId: string;
  eventName: string;
};

// All download audit event types — shared between the orchestrator's
// `ExportDownloadAudit.eventType` and the `DownloadDetail` shape below so
// adding a new resource download only requires updating this union.
export type DownloadAuditEventType =
  | typeof AuditEventType.DocumentDownload
  | typeof AuditEventType.DocChronologyDownload
  | typeof AuditEventType.FactDownload
  | typeof AuditEventType.ChronologyDownload
  | typeof AuditEventType.InconsistencyDownload
  | typeof AuditEventType.PersonDownload
  | typeof AuditEventType.ThreadDownload;

// Download detail — emitted when a user initiates any download/export.
type DownloadDetail = {
  auditEventType: DownloadAuditEventType;
  variant?: string;
  downloadFormat?: string;
  withSourceDocuments?: boolean;
  itemCount?: number;
};

export type AuditTrailFilters = Partial<AuditTrailGetRequestDto>;

export const AUDIT_TRAIL_SORTABLE_COLUMNS = ['eventDate', 'auditEventType', 'user'];

// Auditable fields for update operations - excludes fields handled separately (e.g., userIds, docIds)
export const AUDITABLE_MATTER_FIELDS = [
  'name',
  'description',
  'code',
  'tokenLimit',
  'additionalContext',
] as const satisfies readonly (keyof MatterUpdateMetadataRequestDto)[];

export const AUDITABLE_SUBSET_FIELDS = [
  'name',
] as const satisfies readonly (keyof UpdateSubsetRequestDto)[];

export const AUDITABLE_FACT_FIELDS = [
  'relevance',
  'significance',
  'description',
] as const satisfies readonly (keyof UpdateFactRequestDto)[];

export const AUDITABLE_CHRONOLOGY_FIELDS = [
  'title',
] as const satisfies readonly (keyof UpdateChronologyRequestDto)[];

export const AUDITABLE_DOC_CHRONOLOGY_FIELDS = [
  'title',
] as const satisfies readonly (keyof UpdateDocChronRequestDto)[];

export const AUDITABLE_DOC_CHRON_DOC_FIELDS = [
  'summary',
  'dateText',
  'title',
] as const satisfies readonly (keyof UpdateDocChronDocRequestDto)[];

export enum AuditTrailDownloadColumn {
  EventDate = 'Event Date',
  User = 'User',
  EventType = 'Event Type',
  MatterName = 'Matter',
  Details = 'Details',
}
